import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-8">
          <ArrowLeft size={20} />
          Retour à l'accueil
        </Link>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold mb-8">Politique de Confidentialité</h1>
          <p className="text-muted-foreground mb-8">Dernière mise à jour : Décembre 2025</p>
          
          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                SIMBLYO s'engage à protéger la vie privée de ses utilisateurs. Cette politique de confidentialité 
                explique comment nous collectons, utilisons et protégeons vos données personnelles conformément 
                au Règlement Général sur la Protection des Données (RGPD).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Responsable du traitement</h2>
              <p className="text-muted-foreground leading-relaxed">
                Le responsable du traitement des données est SIMBLYO, dont le siège social est situé en France. 
                Pour toute question relative à vos données personnelles : contact@simblyo.com
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. Données collectées</h2>
              <p className="text-muted-foreground leading-relaxed">Nous collectons les données suivantes :</p>
              <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
                <li><strong>Données d'identification :</strong> nom, prénom, adresse email</li>
                <li><strong>Données de connexion :</strong> adresse IP, logs de connexion, appareil utilisé</li>
                <li><strong>Données d'utilisation :</strong> contenus générés, pages visitées, actions effectuées</li>
                <li><strong>Données de paiement :</strong> traitées par Stripe (nous ne stockons pas vos données bancaires)</li>
                <li><strong>Données professionnelles :</strong> nom d'entreprise, site web, adresse (optionnel)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Finalités du traitement</h2>
              <p className="text-muted-foreground leading-relaxed">Vos données sont utilisées pour :</p>
              <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
                <li>Fournir et améliorer nos services</li>
                <li>Gérer votre compte et vos abonnements</li>
                <li>Personnaliser votre expérience utilisateur</li>
                <li>Vous envoyer des communications relatives au service</li>
                <li>Respecter nos obligations légales</li>
                <li>Prévenir la fraude et assurer la sécurité</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Base légale du traitement</h2>
              <p className="text-muted-foreground leading-relaxed">
                Le traitement de vos données repose sur :
              </p>
              <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
                <li><strong>L'exécution du contrat :</strong> pour fournir nos services</li>
                <li><strong>Le consentement :</strong> pour les cookies non essentiels et les communications marketing</li>
                <li><strong>L'intérêt légitime :</strong> pour améliorer nos services et assurer la sécurité</li>
                <li><strong>L'obligation légale :</strong> pour respecter nos obligations comptables et fiscales</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Durée de conservation</h2>
              <p className="text-muted-foreground leading-relaxed">
                Vos données sont conservées pendant la durée de votre compte, puis 3 ans après sa suppression 
                pour des raisons légales. Les données de facturation sont conservées 10 ans conformément aux 
                obligations comptables.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Partage des données</h2>
              <p className="text-muted-foreground leading-relaxed">
                Vos données peuvent être partagées avec :
              </p>
              <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
                <li><strong>Stripe :</strong> pour le traitement des paiements</li>
                <li><strong>OpenAI :</strong> pour la génération de contenu (données anonymisées)</li>
                <li><strong>Google :</strong> pour l'authentification OAuth (si utilisée)</li>
                <li><strong>Hébergeurs :</strong> pour le stockage sécurisé des données</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                Nous ne vendons jamais vos données personnelles à des tiers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Transferts internationaux</h2>
              <p className="text-muted-foreground leading-relaxed">
                Certains de nos prestataires sont situés hors de l'Union Européenne. Dans ce cas, nous nous 
                assurons que des garanties appropriées sont en place (clauses contractuelles types, certifications).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Vos droits</h2>
              <p className="text-muted-foreground leading-relaxed">
                Conformément au RGPD, vous disposez des droits suivants :
              </p>
              <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
                <li><strong>Droit d'accès :</strong> obtenir une copie de vos données</li>
                <li><strong>Droit de rectification :</strong> corriger vos données inexactes</li>
                <li><strong>Droit à l'effacement :</strong> supprimer vos données</li>
                <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
                <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
                <li><strong>Droit à la limitation :</strong> limiter le traitement de vos données</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                Pour exercer ces droits, contactez-nous à : contact@simblyo.com
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Sécurité</h2>
              <p className="text-muted-foreground leading-relaxed">
                Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger 
                vos données : chiffrement SSL/TLS, authentification sécurisée, accès restreint aux données, 
                sauvegardes régulières.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Réclamation</h2>
              <p className="text-muted-foreground leading-relaxed">
                Si vous estimez que vos droits ne sont pas respectés, vous pouvez déposer une réclamation 
                auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) : www.cnil.fr
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Contact</h2>
              <p className="text-muted-foreground leading-relaxed">
                Pour toute question sur cette politique : contact@simblyo.com
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
